/**
 * Analytics utility for AHP (American Hospital Prices)
 * Pushes events to GTM dataLayer for GA4 tracking.
 * Only fires on AHP brand to avoid polluting other brand analytics.
 */

import { getBrand } from '@/config/brands';
import { isNativeApp } from '@/lib/platform';

// Extend window for dataLayer
declare global {
  interface Window {
    dataLayer?: Record<string, any>[];
  }
}

/** Push an event to GTM dataLayer (AHP web only).
 *  Native app: NO GTM/GA tracking — App Store privacy labels don't declare
 *  it, and the native build ships without a consent-gated GTM container.
 *  Sentry crash reporting is separate and unaffected. */
function pushEvent(event: string, params?: Record<string, any>) {
  if (isNativeApp()) return;
  const brand = getBrand();
  if (brand.key !== 'ahp') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
}

// ─── Classroom Shorts feed ─────────────────────────────────────────────
/** A viewer opened a short on a platform from the Classroom feed
 *  ("youtube" = swapped the card to the YouTube player; "tiktok" = click-through). */
export function trackShortsOpen(platform: 'youtube' | 'tiktok', clipId: string, code?: string) {
  pushEvent('shorts_open', { platform, clip_id: clipId, code });
}

/** The embedded YouTube player reached PLAYING for a clip (once per card mount). */
export function trackShortsPlay(clipId: string, code?: string) {
  pushEvent('shorts_play', { clip_id: clipId, code });
}

// ─── Page Views ───────────────────────────────────────────────────────

export function trackPageView(path: string, title?: string) {
  pushEvent('page_view', {
    page_path: path,
    page_title: title || document.title,
  });
}

// ─── Search ───────────────────────────────────────────────────────────

export function trackSearch(query: string, category?: string) {
  pushEvent('search', {
    search_term: query,
    search_category: category, // procedure, hospital, scenario
  });
}

export function trackSearchResultClick(resultType: string, resultId: string, resultName: string, position?: number) {
  pushEvent('select_search_result', {
    result_type: resultType, // hospital, procedure, scenario
    result_id: resultId,
    result_name: resultName,
    result_position: position,
  });
}

// ─── Hospital ─────────────────────────────────────────────────────────

export function trackHospitalView(hospitalId: string, hospitalName: string, state?: string) {
  pushEvent('view_hospital', {
    hospital_id: hospitalId,
    hospital_name: hospitalName,
    hospital_state: state,
  });
}

export function trackHospitalTabChange(hospitalId: string, tab: string) {
  pushEvent('hospital_tab_change', {
    hospital_id: hospitalId,
    tab_name: tab,
  });
}

// ─── Procedures ───────────────────────────────────────────────────────

export function trackProcedureView(codeType: string, code: string, name?: string) {
  pushEvent('view_procedure', {
    code_type: codeType,
    procedure_code: code,
    procedure_name: name,
  });
}

// ─── Scenarios ────────────────────────────────────────────────────────

export function trackScenarioView(scenarioId: string, scenarioName: string) {
  pushEvent('view_scenario', {
    scenario_id: scenarioId,
    scenario_name: scenarioName,
  });
}

export function trackScenarioBookmark(scenarioId: string, scenarioName: string, bookmarked: boolean) {
  pushEvent('scenario_bookmark', {
    scenario_id: scenarioId,
    scenario_name: scenarioName,
    bookmarked,
  });
}

export function trackScenarioShare(scenarioId: string, method?: string) {
  pushEvent('share', {
    content_type: 'scenario',
    item_id: scenarioId,
    method: method || 'copy_link',
  });
}

export function trackScenarioHospitalSelect(scenarioId: string, hospitalId: string, hospitalName: string, price?: number) {
  pushEvent('scenario_hospital_select', {
    scenario_id: scenarioId,
    hospital_id: hospitalId,
    hospital_name: hospitalName,
    scenario_price: price,
  });
}

// ─── Rankings ─────────────────────────────────────────────────────────

export function trackRankingsFilter(filterType: string, filterValue: string) {
  pushEvent('rankings_filter', {
    filter_type: filterType, // state, scenario, sort, code
    filter_value: filterValue,
  });
}

export function trackRankingsHospitalClick(hospitalId: string, hospitalName: string, rank?: number) {
  pushEvent('rankings_hospital_click', {
    hospital_id: hospitalId,
    hospital_name: hospitalName,
    rank_position: rank,
  });
}

// ─── Care Plan Funnel ─────────────────────────────────────────────────

export function trackCarePlanStart() {
  pushEvent('care_plan_start');
}

export function trackCarePlanStep(step: number, stepName: string) {
  pushEvent('care_plan_step', {
    step_number: step,
    step_name: stepName,
  });
}

export function trackCarePlanScenarioToggle(scenarioId: string, added: boolean) {
  pushEvent('care_plan_scenario_toggle', {
    scenario_id: scenarioId,
    action: added ? 'add' : 'remove',
  });
}

export function trackCarePlanCreate(planId: string, scenarioCount: number) {
  pushEvent('care_plan_create', {
    plan_id: planId,
    scenario_count: scenarioCount,
  });
}

export function trackCheckoutStart(planId: string, value?: number) {
  pushEvent('begin_checkout', {
    plan_id: planId,
    value,
    currency: 'USD',
  });
}

export function trackCheckoutSuccess(planId: string, value?: number) {
  pushEvent('purchase', {
    transaction_id: planId,
    value,
    currency: 'USD',
    items: [{ item_name: 'Care Plan', item_category: 'care_plan' }],
  });
}

// ─── Merch ────────────────────────────────────────────────────────────

export function trackAddToCart(itemName: string, price: number, quantity: number) {
  pushEvent('add_to_cart', {
    currency: 'USD',
    value: price * quantity,
    items: [{ item_name: itemName, price, quantity }],
  });
}

export function trackRemoveFromCart(itemName: string) {
  pushEvent('remove_from_cart', {
    items: [{ item_name: itemName }],
  });
}

export function trackMerchCheckout(cartValue: number, itemCount: number) {
  pushEvent('begin_checkout', {
    currency: 'USD',
    value: cartValue,
    items_count: itemCount,
    checkout_type: 'merch',
  });
}

// ─── Subscriptions & Follow ───────────────────────────────────────────

export function trackEmailSubscribe(topics?: string[]) {
  pushEvent('email_subscribe', {
    subscription_topics: topics?.join(','),
  });
}

export function trackEmailUnsubscribe() {
  pushEvent('email_unsubscribe');
}

export function trackPushSubscribe(topics?: string[]) {
  pushEvent('push_subscribe', {
    subscription_topics: topics?.join(','),
  });
}

export function trackPushUnsubscribe() {
  pushEvent('push_unsubscribe');
}

// ─── Engagement ───────────────────────────────────────────────────────

export function trackDonate(method?: string) {
  pushEvent('donate_click', { method });
}

export function trackInstallApp(platform?: string) {
  pushEvent('install_app', { platform });
}

export function trackLanguageChange(language: string) {
  pushEvent('language_change', { language });
}

export function trackDarkModeToggle(enabled: boolean) {
  pushEvent('dark_mode_toggle', { enabled });
}

export function trackCookieConsent(action: string) {
  pushEvent('cookie_consent', { consent_action: action }); // accept_all, necessary_only, custom
}

export function trackMapInteraction(action: string, state?: string) {
  pushEvent('map_interaction', {
    map_action: action, // pan, zoom, state_click, hospital_click
    map_state: state,
  });
}

export function trackOutboundClick(url: string, label?: string) {
  pushEvent('outbound_click', {
    link_url: url,
    link_label: label,
  });
}
